import React, { useEffect, useState } from 'react';
import { Check, Loader2, Truck } from 'lucide-react';
import { fetchStoreOrders, markOrderShipped, type StoreOrder } from '@/services/creatorApi';

// Orders for a creator's products, on My Store. Physical orders show where to ship
// and a "Mark shipped" button: that emails the buyer and releases the money
// (nothing pays out before it). Digital orders are delivered on their own.
// Only appears once the creator has a product.

const INK = 'var(--hj-ink)';
const MUTED = 'var(--hj-ink-2)';
const money = (c: number) => `$${(c / 100).toFixed(2)}`;
const day = (s: string) => new Date(s).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

const Row: React.FC<{ o: StoreOrder; onShipped: (o: StoreOrder) => void }> = ({ o, onShipped }) => {
  const [open, setOpen] = useState(false);
  const [carrier, setCarrier] = useState('');
  const [tracking, setTracking] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const a = o.shipping?.address;
  const shipped = o.fulfillment?.status === 'shipped';
  const toShip = o.kind === 'physical' && !shipped && !o.refunded;
  const ship = async () => {
    setBusy(true); setErr(null);
    try { await markOrderShipped(o.id, carrier, tracking); onShipped({ ...o, fulfillment: { status: 'shipped', carrier, tracking, shippedAt: new Date().toISOString() } }); setOpen(false); }
    catch (e) { setErr((e as Error).message); }
    setBusy(false);
  };
  return (
    <li style={{ padding: '14px 0', borderTop: '1px solid var(--hj-hairline-soft)' }}>
      <div className="flex flex-wrap items-start justify-between" style={{ gap: 12 }}>
        <div style={{ minWidth: 0, flex: '1 1 260px' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: INK }}>
            {o.title}{o.optionLabel ? ` · ${o.optionName || 'Option'} ${o.optionLabel}` : ''}{o.quantity > 1 ? ` × ${o.quantity}` : ''}
          </div>
          <div style={{ fontSize: 14, color: MUTED, marginTop: 2 }}>
            {o.buyerName || o.buyerEmail} · {money(o.amountPaid)} · {day(o.purchasedAt)}
            {o.payoutCents ? ` · you get ${money(o.payoutCents)}` : ''}
          </div>
          {o.kind === 'physical' && a && (
            <div style={{ fontSize: 14, color: INK, marginTop: 6, lineHeight: 1.45 }}>
              {o.shipping?.name}<br />{a.line1}{a.line2 ? `, ${a.line2}` : ''}<br />{[a.city, a.state, a.postal_code].filter(Boolean).join(' ')}
            </div>
          )}
        </div>
        <div style={{ flex: 'none' }}>
          {o.refunded ? <span style={{ fontSize: 14, fontWeight: 600, color: INK }}>Refunded</span>
            : o.kind === 'digital' ? <span style={{ fontSize: 14, fontWeight: 600, color: INK }}>Downloaded</span>
            : shipped ? <span className="inline-flex items-center" style={{ gap: 6, fontSize: 14, fontWeight: 600, color: INK }}><Check size={16} /> Shipped{o.fulfillment?.tracking ? ` · ${o.fulfillment.tracking}` : ''}</span>
            : <button type="button" onClick={() => setOpen((v) => !v)} className="inline-flex items-center" style={{ gap: 6, minHeight: 44, padding: '10px 16px', borderRadius: 10, border: 0, background: INK, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}><Truck size={16} /> Mark shipped</button>}
        </div>
      </div>
      {toShip && open && (
        <div className="flex flex-wrap items-center" style={{ gap: 8, marginTop: 10 }}>
          <input value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder="Carrier (optional)" aria-label="Carrier" style={{ flex: '1 1 140px', minWidth: 0, padding: '9px 12px', borderRadius: 10, border: '1px solid var(--hj-hairline)', fontSize: 14, fontFamily: 'inherit', background: '#fff', color: INK }} />
          <input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="Tracking number (optional)" aria-label="Tracking number" style={{ flex: '2 1 200px', minWidth: 0, padding: '9px 12px', borderRadius: 10, border: '1px solid var(--hj-hairline)', fontSize: 14, fontFamily: 'inherit', background: '#fff', color: INK }} />
          <button type="button" disabled={busy} onClick={ship} style={{ minHeight: 40, padding: '8px 16px', borderRadius: 10, border: 0, background: 'var(--hj-yellow)', color: 'oklch(0.18 0.006 70)', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Shipped: tell the buyer'}
          </button>
          {err && <span style={{ fontSize: 13.5, color: 'var(--hj-pink-ink)', flex: '1 1 100%' }}>{err}</span>}
          <span style={{ fontSize: 13, color: MUTED, flex: '1 1 100%' }}>We email the buyer and release your money for this order.</span>
        </div>
      )}
    </li>
  );
};

const StoreOrdersPanel: React.FC = () => {
  const [orders, setOrders] = useState<StoreOrder[] | null>(null);
  const [hasProducts, setHasProducts] = useState(false);
  useEffect(() => { fetchStoreOrders().then((r) => { setOrders(r.orders); setHasProducts(r.hasProducts); }).catch(() => setOrders([])); }, []);
  if (!orders || !hasProducts) return null;
  const toShip = orders.filter((o) => o.kind === 'physical' && o.fulfillment?.status !== 'shipped' && !o.refunded).length;
  return (
    <section style={{ background: '#fff', border: '1px solid var(--hj-hairline-soft)', borderRadius: 16, padding: 20, marginBottom: 24 }}>
      <h2 className="font-hj-display m-0" style={{ fontSize: 18, color: INK, letterSpacing: '-0.02em', fontWeight: 700 }}>
        Orders{toShip ? ` · ${toShip} to ship` : ''}
      </h2>
      {orders.length === 0 ? <p style={{ fontSize: 14, color: MUTED, margin: '8px 0 0' }}>No orders yet. They show up here with the address to ship to.</p> : (
        <ul className="m-0 p-0" style={{ listStyle: 'none', marginTop: 6 }}>
          {orders.slice(0, 50).map((o) => <Row key={o.id} o={o} onShipped={(n) => setOrders((l) => (l || []).map((x) => (x.id === n.id ? n : x)))} />)}
        </ul>
      )}
    </section>
  );
};

export default StoreOrdersPanel;
